import type { Chosen, WorksheetPaper } from "./worksheet-state.js";

/**
 * Where everything on a sheet goes, in millimetres.
 *
 * The sheet is laid out here rather than left to the browser's page breaks,
 * because the browser will happily print a character's model on one page and
 * its practice squares on the next. Every block's height is known before it is
 * drawn, so the pages are filled here a whole block at a time, and each page is
 * drawn as a box exactly the size of the paper.
 *
 * Millimetres throughout, because that is what a teacher measures a square in
 * and what `@page` and CSS both print at actual size.
 */

/** A sheet of paper, portrait. */
export interface PaperSize {
  readonly width: number;
  readonly height: number;
}

export const paperSizes: Readonly<Record<WorksheetPaper, PaperSize>> = {
  a4: { width: 210, height: 297 },
  letter: { width: 215.9, height: 279.4 },
};

/** The margin on every side, inside what any office printer can reach. */
export const pageMargin = 12;

/** The line at the foot of every page. */
export const footerHeight = 6;

/** The heading at the top of each part of the sheet. */
export const titleHeight = 11;

/** The line naming the character above its squares. */
export const headHeight = 7;

/** The space after each block. */
export const blockGap = 4;

/** One character on the stroke order pages: its square, and the box around it. */
export interface KeySize {
  readonly width: number;
  readonly glyph: number;
  readonly height: number;
}

/**
 * The two sizes a character takes on the stroke order pages.
 *
 * 54 mm squares, three across and four down either paper, for a character
 * of up to `complexStrokes` strokes. Anything denser takes a 76 mm square,
 * two across and three down, since 新 at 54 mm put thirteen numbers in a
 * space the size of a postage stamp and they could not be followed. The
 * numbers and arrows are drawn on the outline grid, so they grow with the
 * square.
 */
export const keySizes: Readonly<Record<"simple" | "complex", KeySize>> = {
  simple: { width: 62, glyph: 54, height: 63 },
  complex: { width: 93, glyph: 76, height: 84 },
};

/** The most strokes a character can have and keep the smaller square. */
export const complexStrokes = 10;

/** The square a character takes on the stroke order pages. */
export function keySize(strokeCount: number | undefined): KeySize {
  return (strokeCount ?? 0) > complexStrokes
    ? keySizes.complex
    : keySizes.simple;
}

/**
 * Pack boxes of different widths into rows, in order, as many as fit.
 * @returns The indices of the boxes in each row.
 */
export function packRows(
  widths: readonly number[],
  room: number,
): readonly (readonly number[])[] {
  const rows: number[][] = [];
  let row: number[] = [];
  let left = room;

  for (const [at, width] of widths.entries()) {
    if (row.length > 0 && width > left + 0.01) {
      rows.push(row);
      row = [];
      left = room;
    }
    row.push(at);
    left -= width;
  }
  if (row.length > 0) {
    rows.push(row);
  }

  return rows;
}

/** The measurements every block on one sheet shares. */
export interface SheetMetrics {
  readonly paper: PaperSize;

  /** The width inside the margins. */
  readonly width: number;

  /** The height inside the margins, less the footer. */
  readonly height: number;

  /** The side of one practice square. */
  readonly cell: number;

  /** How many practice squares fit across. */
  readonly cellsAcross: number;

  /** The side of one square in the stroke order row. */
  readonly strokeCell: number;

  /** How many of those fit across. */
  readonly strokesAcross: number;
}

/**
 * The practice square a sheet aims for, in millimetres.
 *
 * 16 mm was the default while the size was a setting, and a character of
 * more than `complexStrokes` strokes asks for 20 mm, since its strokes are
 * closer together. One character like that sets the size for the whole
 * sheet, because squares of two sizes on one page do not line up.
 */
export const squareTargets = { simple: 16, complex: 20 } as const;

function complex(strokeCount: number | undefined): boolean {
  return (strokeCount ?? 0) > complexStrokes;
}

/**
 * The measurements for a sheet of these characters.
 *
 * The square is stretched a little from its target so that a whole number
 * of them fills the width, which leaves no ragged gap at the end of a row.
 * The stroke order squares are a little over half a practice square, which
 * keeps a twenty-stroke character to one row on either paper, and never
 * under 6 mm, below which a stroke cannot be told from the one before it.
 *
 * A square size chosen under More settings is printed at exactly that size,
 * since a teacher who asks for 14 mm will measure it, and the row ends where
 * the last whole square does.
 * @param strokeCounts - How many strokes each character has, or nothing for
 * one with no outlines.
 * @param chosen - The square size asked for, or `auto`.
 */
export function sheetMetrics(
  paper: WorksheetPaper,
  strokeCounts: readonly (number | undefined)[],
  chosen: Chosen = "auto",
): SheetMetrics {
  const size = paperSizes[paper];
  const width = size.width - 2 * pageMargin;
  const target = strokeCounts.some(complex)
    ? squareTargets.complex
    : squareTargets.simple;
  const cellsAcross =
    chosen === "auto" ? Math.round(width / target) : Math.floor(width / chosen);
  const cell = chosen === "auto" ? width / cellsAcross : chosen;
  const strokeCell = Math.max(6, Math.min(9, cell * 0.55));

  return {
    paper: size,
    width,
    height: size.height - 2 * pageMargin - footerHeight,
    cell,
    cellsAcross,
    strokeCell,
    strokesAcross: Math.floor(width / strokeCell),
  };
}

/**
 * How a character is practised: how many copies to trace after the model,
 * and how many rows of squares it gets.
 *
 * Three copies and one row of a dozen squares is a page of ten characters.
 * A character of more than `complexStrokes` strokes takes a fourth copy,
 * since there is more of it to get wrong, and still one row. It used to take
 * a second row of empty squares as well. That printed a row nobody had asked
 * for under every dense character, and halved how many fitted a page. Either
 * can be chosen under More settings instead, for every character alike.
 */
export function practiceFor(
  strokeCount: number | undefined,
  chosen: { readonly trace: Chosen; readonly rows: Chosen } = {
    trace: "auto",
    rows: "auto",
  },
): {
  readonly trace: number;
  readonly rows: number;
} {
  return {
    trace:
      chosen.trace === "auto" ? (complex(strokeCount) ? 4 : 3) : chosen.trace,
    rows: chosen.rows === "auto" ? 1 : chosen.rows,
  };
}

/** How tall the stroke order row over a practice block is. */
function strokeOrderHeight(
  metrics: SheetMetrics,
  strokeCount: number | undefined,
): number {
  if (strokeCount === undefined) {
    return 0;
  }
  const rows = Math.ceil(strokeCount / metrics.strokesAcross);

  return rows === 0 ? 0 : rows * metrics.strokeCell + 1.5;
}

/**
 * How tall one character's practice block is.
 * @param strokeCount - How many strokes its outlines have, or nothing where
 * there are no outlines to draw them from.
 */
export function practiceBlockHeight(
  metrics: SheetMetrics,
  strokeCount: number | undefined,
  rows: number = practiceFor(strokeCount).rows,
): number {
  return (
    headHeight +
    strokeOrderHeight(metrics, strokeCount) +
    rows * metrics.cell +
    blockGap
  );
}

/** How tall one character's recall block is: its line and a row of squares. */
export function recallBlockHeight(metrics: SheetMetrics): number {
  return headHeight + metrics.cell + blockGap;
}

/**
 * Deal blocks onto pages without splitting one.
 *
 * A block taller than a page gets a page to itself, and is cut off at its
 * foot rather than lost. No setting this tool offers makes one that tall.
 * @param heights - Each block's height, in order.
 * @param room - The height a page has for blocks, the first page and the rest.
 * @returns The indices of the blocks on each page.
 */
export function paginate(
  heights: readonly number[],
  room: { readonly first: number; readonly rest: number },
): readonly (readonly number[])[] {
  const pages: number[][] = [];
  let page: number[] = [];
  let left = room.first;

  for (const [at, height] of heights.entries()) {
    if (page.length > 0 && height > left) {
      pages.push(page);
      page = [];
      left = room.rest;
    }
    page.push(at);
    left -= height;
  }
  if (page.length > 0) {
    pages.push(page);
  }

  return pages;
}
